<template>
    <div class="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 class="font-header text-[1.5rem] font-bold mb-4">Page Description</h3>
        <p class="font-body text-[1.125rem] leading-[1.75] text-gray-600 mb-4">Edit the description that appears on the
            {{ pageTitle }} page.</p>
        <div class="space-y-4">
            <div>
                <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Description</label>
                <div class="border border-gray-300 rounded-md">
                    <RichTextEditor ref="editor" :content="pageDescription" @update:content="pageDescription = $event"
                        placeholder="Write your description here..." />
                </div>
            </div>
            <button @click="savePageDescription"
                class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75]">
                Save Description
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { adminService } from '../../services/admin'
import { publicService } from '../../services/public'
import RichTextEditor from '../common/RichTextEditor.vue'
import { convertLegacyQuillContent } from '../../utils/quillConfig'

const props = defineProps({
    pageTitle: {
        type: String,
        required: true
    },
    defaultDescription: {
        type: String,
        default: ''
    }
})

const editor = ref(null)
const pageDescription = ref(convertLegacyQuillContent(props.defaultDescription || ''))
const pageId = ref(null)

const loadPageDescription = async () => {
    try {
        const page = await publicService.getPage(props.pageTitle)
        pageId.value = page.id
        pageDescription.value = convertLegacyQuillContent(page.description || props.defaultDescription || '')
        await nextTick()
        if (editor.value) {
            editor.value.setHTML(pageDescription.value || '')
        }
    } catch (error) {
        console.error(`Failed to load page description for ${props.pageTitle}:`, error)
        pageId.value = null
        pageDescription.value = convertLegacyQuillContent(props.defaultDescription || '')
        await nextTick()
        if (editor.value) {
            editor.value.setHTML(pageDescription.value || '')
        }
    }
}

const savePageDescription = async () => {
    try {
        const data = {
            description: pageDescription.value.trim() || null
        }

        if (pageId.value) {
            await adminService.updatePage(pageId.value, data)
        } else {
            await adminService.createPage({
                title: props.pageTitle,
                description: pageDescription.value.trim() || null
            })
        }
        alert('Page description saved successfully!')
        await loadPageDescription()
    } catch (error) {
        console.error('Failed to save page description:', error)
        alert('Failed to save page description. Please try again.')
    }
}

onMounted(() => {
    loadPageDescription()
})
</script>

<style scoped>
:deep(.ql-editor) {
    min-height: 100px;
    line-height: 1.75;
    color: #253439;
    direction: ltr !important;
    text-align: left !important;
    unicode-bidi: embed !important;
}

:deep(.ql-container) {
    font-family: 'Georgia', serif;
    direction: ltr !important;
    unicode-bidi: embed !important;
    border-bottom-left-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
}

:deep(.ql-toolbar) {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
    background-color: #FAFAFA;
}

:deep(.ql-editor *) {
    direction: ltr !important;
}
</style>
